import { motion, AnimatePresence } from "framer-motion";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import {
    Shield,
    UserPlus,
    UserMinus,
    Ban,
    AlertTriangle,
    Brain,
    Trash2,
    Lock,
    CheckCircle2,
    XCircle,
    Settings2,
    Bell,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ar } from "date-fns/locale";

const sevColor = {
    safe: { stripe: "bg-emerald-400", chip: "text-emerald-300 bg-emerald-500/10 border-emerald-500/20" },
    info: { stripe: "bg-cyan-400", chip: "text-cyan-300 bg-cyan-500/10 border-cyan-500/20" },
    warning: { stripe: "bg-amber-400", chip: "text-amber-300 bg-amber-500/10 border-amber-500/20" },
    critical: { stripe: "bg-rose-400", chip: "text-rose-300 bg-rose-500/10 border-rose-500/20" },
};

const typeIcon = {
    join: UserPlus,
    leave: UserMinus,
    spam_blocked: Trash2,
    flood_detected: AlertTriangle,
    ai_scam_caught: Brain,
    raid_detected: Shield,
    captcha_solved: CheckCircle2,
    captcha_failed: XCircle,
    captcha_timeout: XCircle,
    ban: Ban,
    auto_ban_blacklist: Ban,
    ban_blacklist_msg: Ban,
    settings_changed: Settings2,
    bot_added: Shield,
    bot_removed: Shield,
    mute: Lock,
};

const formatTs = (ts) => {
    try {
        return formatDistanceToNow(new Date(ts), { addSuffix: true, locale: ar });
    } catch {
        return ts;
    }
};

export const EventFeed = ({ events = [], height = "h-[460px]", emptyHint }) => {
    return (
        <ScrollArea className={`${height} pr-2`} data-testid="event-feed-scroll">
            <div className="space-y-2" data-testid="event-feed-list">
                <AnimatePresence initial={false}>
                    {events.length === 0 ? (
                        <div
                            className="py-8 text-center text-sm text-muted-foreground"
                            data-testid="event-feed-empty"
                        >
                            {emptyHint ||
                                "لا توجد أحداث بعد. ستظهر هنا لحظة وصولها."}
                        </div>
                    ) : (
                        events.map((e, idx) => {
                            const sev = sevColor[e.severity] || sevColor.info;
                            const Icon = typeIcon[e.event_type] || Bell;
                            return (
                                <motion.div
                                    key={e.id || idx}
                                    initial={{ opacity: 0, y: -8 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0 }}
                                    transition={{
                                        duration: 0.2,
                                        ease: "easeOut",
                                    }}
                                    className="flex gap-3 rounded-lg border border-border/60 bg-card/40 p-3 transition-colors duration-150 hover:bg-card/60"
                                    data-testid="live-event-row"
                                >
                                    <div
                                        className={`w-1 rounded-full ${sev.stripe}`}
                                        data-testid="live-event-severity"
                                    />
                                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted/40">
                                        <Icon className="h-4 w-4 text-muted-foreground" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <Badge
                                                variant="outline"
                                                className={`text-[10px] font-mono ${sev.chip}`}
                                            >
                                                {e.event_type}
                                            </Badge>
                                            {e.user_is_premium && (
                                                <Badge
                                                    variant="outline"
                                                    className="text-[10px] border-amber-500/30 text-amber-300 bg-amber-500/10"
                                                >
                                                    💎 Premium
                                                </Badge>
                                            )}
                                            <span
                                                className="text-[10px] text-muted-foreground font-mono"
                                                dir="ltr"
                                            >
                                                {formatTs(e.ts)}
                                            </span>
                                        </div>
                                        <div className="mt-1 text-sm leading-snug">
                                            {e.reason || "—"}
                                        </div>
                                        {e.chat_title && (
                                            <div className="mt-1 text-[11px] text-muted-foreground">
                                                <span className="font-mono">
                                                    {e.chat_title}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </motion.div>
                            );
                        })
                    )}
                </AnimatePresence>
            </div>
        </ScrollArea>
    );
};

export default EventFeed;
